import { useEffect, useState } from 'react';
import { BellRing, Mail, Send, Save } from 'lucide-react';
import { api, jsonBody } from './api';

type ChannelName = 'telegram' | 'resend';
interface Channel {
  channel: ChannelName; configured: boolean; enabled: boolean;
  chatId?: string; from?: string; to?: string; defaultSubject?: string;
}
type Notify = (type: 'success' | 'error', message: string) => void;

export function AccountNotifications({ notify }: { notify: Notify }) {
  const [channels, setChannels] = useState<Channel[] | null>(null);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setError('');
    api<{ items: Channel[] }>('/api/account/notification-channels', { signal: controller.signal })
      .then((result) => { if (!controller.signal.aborted) setChannels(result.items); })
      .catch((reason) => { if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : '加载失败'); });
    return () => controller.abort();
  }, [revision]);
  return <div className="page-stack tenant-page">
    {error && <div className="form-error" role="alert">{error}<button className="text-button" onClick={() => setRevision((value) => value + 1)}>重试</button></div>}
    {!channels && !error && <div className="panel-loader" role="status">正在加载通知配置…</div>}
    {channels && <div className="channel-grid">{(['telegram', 'resend'] as const).map((channel) => <NotificationForm key={channel} initial={channels.find((item) => item.channel === channel) ?? { channel, configured: false, enabled: false }} notify={notify} />)}</div>}
  </div>;
}

function NotificationForm({ initial, notify }: { initial: Channel; notify: Notify }) {
  const telegram = initial.channel === 'telegram';
  const name = telegram ? 'Telegram' : 'Resend 邮件';
  const Icon = telegram ? BellRing : Mail;
  const [saved, setSaved] = useState(initial);
  const [form, setForm] = useState({ botToken: '', apiKey: '', chatId: initial.chatId ?? '', from: initial.from ?? '', to: initial.to ?? '', defaultSubject: initial.defaultSubject ?? 'EdgeDock 通知', enabled: initial.enabled });
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const base = `/api/account/notification-channels/${initial.channel}`;
  const act = async (action: 'save' | 'test') => {
    if (busy) return;
    setBusy(action); setFeedback(null);
    try {
      if (action === 'save') {
        const result = await api<Channel>(base, { method: 'PUT', body: jsonBody(telegram ? { botToken: form.botToken, chatId: form.chatId, enabled: form.enabled } : { apiKey: form.apiKey, from: form.from, to: form.to, defaultSubject: form.defaultSubject, enabled: form.enabled }) });
        setSaved(result);
        setForm((current) => ({ ...current, botToken: '', apiKey: '' }));
      } else await api(`${base}/test`, { method: 'POST', body: '{}' });
      const text = action === 'save' ? '配置已保存' : telegram ? 'Telegram 测试消息已发送' : 'Resend 已接收测试邮件';
      setFeedback({ type: 'success', text });
    } catch (reason) {
      const text = reason instanceof Error ? reason.message : '操作失败，请重试';
      setFeedback({ type: 'error', text }); notify('error', text);
    } finally { setBusy(null); }
  };
  return <form className="panel channel-card" aria-label={`${name} 配置`} onSubmit={(event) => { event.preventDefault(); void act('save'); }}>
    <header><span className="channel-mark"><Icon size={22} /></span><div><h3>{name}</h3><span>{telegram ? '向固定会话发送消息' : '向固定邮箱发送邮件'}</span></div><span className={`state-pill ${saved.enabled && saved.configured ? 'active' : 'disabled'}`}>{!saved.configured ? '未配置' : saved.enabled ? '已启用' : '已配置 · 停用'}</span></header>
    <fieldset className="stack-form channel-fields" disabled={busy !== null}>
      {telegram ? <>
        <label>Bot Token<input type="password" autoComplete="new-password" required={!saved.configured} value={form.botToken} onChange={(event) => setForm({ ...form, botToken: event.target.value })} placeholder={saved.configured ? '留空保留现有凭据' : '123456:ABC…'} /></label>
        <label>Chat ID<input required value={form.chatId} onChange={(event) => setForm({ ...form, chatId: event.target.value })} placeholder="例如 -100123456789" /></label>
      </> : <>
        <label>API Key<input type="password" autoComplete="new-password" required={!saved.configured} value={form.apiKey} onChange={(event) => setForm({ ...form, apiKey: event.target.value })} placeholder={saved.configured ? '留空保留现有凭据' : 're_…'} /></label>
        <label>发件邮箱<input type="email" required value={form.from} onChange={(event) => setForm({ ...form, from: event.target.value })} placeholder="notify@example.com" /></label>
        <label>固定收件邮箱<input type="email" required value={form.to} onChange={(event) => setForm({ ...form, to: event.target.value })} placeholder="you@example.com" /></label>
        <label>默认主题<input maxLength={200} required value={form.defaultSubject} onChange={(event) => setForm({ ...form, defaultSubject: event.target.value })} /></label>
      </>}
      <label className="switch-row"><div><strong>启用通道</strong><span>允许节点通过此通道发送通知，不影响手动测试。</span></div><input type="checkbox" checked={form.enabled} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} /></label>
    </fieldset>
    <div className="channel-feedback" aria-live="polite">{busy === 'test' ? <span>正在发送测试…</span> : feedback ? <span className={feedback.type}>{feedback.text}</span> : <span>测试使用最近保存的配置。</span>}</div>
    <footer><button className="primary-button" disabled={busy !== null}><Save size={16} />{busy === 'save' ? '正在保存…' : '保存配置'}</button><button type="button" className="secondary-button" disabled={!saved.configured || busy !== null} onClick={() => void act('test')}><Send size={16} />{busy === 'test' ? '测试中…' : '发送测试'}</button></footer>
  </form>;
}
